/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultCalendarSelection } from "./utils/getDefaultCalendarSelection";
import { getCalendarParameterSelection } from "./utils/getCalendarParameterSelection";
import { getCalendarOriginDictionary } from "./utils/getCalendarOriginDictionary";

interface Parameters {
  commodities: number[];
  calendar: number[];
  calendarOrigin: DictionaryItem[];
  units: number[];
}

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResult$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(
    undefined,
  );
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters.pipe(debounceTime(300)).subscribe(async (parameters) => {
      if (parameters) {
        this.readyState = ComponentContextReadyState.Loading;
        this.loading$.next(true);
        this.olapResult$.next(null);

        const olapResultPromise = this.query(parameters);
        const sourcesPromise = this.fetchSources(parameters);

        const [olapResult, sources] = await Promise.all([olapResultPromise, sourcesPromise]);

        this.olapResult$.next(olapResult);
        this.sources$.next(sources);
        this.readyState = ComponentContextReadyState.Loaded;
        this.loading$.next(false);
      }
    });
  }

  private async fetchSources(parameters: Parameters) {
    const { commodities } = parameters;
    const { dataSourcesService } = this.options;

    const data = await Promise.all(
      commodities.map((commodity) => dataSourcesService.getDataSources(SLIDE_NUMBER, 1, Number(commodity))),
    );
    const sources = data.flatMap((x) => x.split(", "));

    return uniq(sources).join(", ");
  }

  private async _initDefaultMark() {
    const { dataflowService, serviceProvider } = this.options;
    const commoditiesParameter = dataflowService.getParameter("@commodities") as MultiValueParameter;
    const calendarParameter = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const unitsParameter = dataflowService.getParameter("@units") as MultiValueParameter;

    const commodityId = commoditiesParameter.value as number[];

    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);

    const { calendarOrigin } = await getCalendarOriginDictionary(dictionariesService);
    const [{ calendar }] = await Promise.all([
      getDefaultCalendarSelection(commodityId, calendarOrigin, dictionariesService),
    ]);

    calendarParameter.value = calendar;

    this._parameters.next({
      commodities: commoditiesParameter.value as number[],
      calendar: calendarParameter.value as number[],
      units: unitsParameter.value as number[],
      calendarOrigin,
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as number[];
    const commodities = command.values.get("@commodities") as number[];
    const units = command.values.get("@units") as number[];
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }

      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }

      if (units?.length) {
        parameters.units = units;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters) {
    const { commodities, calendar, units, calendarOrigin } = parameters;

    const cubeSlideDef = await dataService.getCubeDefinition(`slide_${SLIDE_NUMBER}`);
    const cubeParameters = clone(cubeSlideDef.parameters);

    const calendarSelection = getCalendarParameterSelection(calendar, calendarOrigin);

    cubeParameters["p_calendar"]!["defaultValue"] = calendarSelection.join(",");
    cubeParameters["p_units"]!["defaultValue"] = units.join(",");
    cubeParameters["p_commodities"]!["defaultValue"] = commodities.join(",");

    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = {
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "commodities.default",
            selection: commodities,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "calendar.default", selection: calendarSelection, selectionMode: SelectionMode.Include },
          {
            path: "units.default",
            selection: units,
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
              { id: "search", aggregationType: AggregationType.None },
            ],
          },
        ].filter(Boolean),
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any;

    const sourcesPromise = this.fetchSources(parameters);
    const olapResultPromise = cubeSlide.query(olapQuery);

    const [sources, olapResult] = await Promise.all([sourcesPromise, olapResultPromise]);

    this.sources$.next(sources);

    return olapResult;
  }
}
